import React from "react";
import { useTranslation } from "react-i18next";
import { InboxOutlined } from "@ant-design/icons";
import type {
  BreakdownView,
  ComponentsView,
  ResultViewKey,
  ResultViewsMap,
  TrendView,
  ValueVsTargetView,
} from "./formulaBuilder.types";
import { STATUS_LABELS, TRAFFIC_LIGHT_COLORS } from "./formulaBuilder.utils";

const formatTrendDate = (iso: string) => {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, { month: "short", day: "2-digit" });
};

const DeltaBadge: React.FC<{ delta: number | null | undefined }> = ({
  delta,
}) => {
  const { t } = useTranslation("formulaBuilder");
  if (delta === null || delta === undefined) return null;
  const positive = delta >= 0;
  return (
    <span
      className="formula-trend-delta"
      style={{ color: positive ? "#16a34a" : "#dc2626" }}
    >
      {positive ? "↗" : "↘"} {positive ? "+" : ""}
      {delta} {t("result.vsLastCycle")}
    </span>
  );
};

const ValueVsTargetCard: React.FC<{ title: string; view: ValueVsTargetView }> = ({
  title,
  view,
}) => {
  const tl = TRAFFIC_LIGHT_COLORS[view.traffic_light] ?? TRAFFIC_LIGHT_COLORS.yellow;
  const statusLabel = STATUS_LABELS[view.status] ?? view.status;
  const target = view.target ?? 0;
  const pct = target > 0 ? Math.min(100, (view.value / target) * 100) : 0;

  return (
    <div className="formula-result-card">
      <h4>{title}</h4>
      <div className="formula-value-row">
        <span className="formula-value-main">
          {view.value}
          {view.target != null && (
            <span className="formula-value-target"> / {view.target}</span>
          )}
        </span>
        <span
          className="formula-status-pill"
          style={{ color: tl.color, background: tl.bg }}
        >
          {statusLabel}
        </span>
      </div>
      <div className="bar-track formula-value-track">
        <div
          className="formula-value-bar-fill"
          style={{ width: `${pct}%`, background: tl.color }}
        />
      </div>
      <DeltaBadge delta={view.change_vs_last_cycle} />
    </div>
  );
};

const TrendCard: React.FC<{ title: string; view: TrendView }> = ({
  title,
  view,
}) => {
  const { t } = useTranslation("formulaBuilder");
  const points = view.points ?? [];
  const latest = points[points.length - 1];
  const previous = points[points.length - 2];
  const delta =
    latest && previous ? Number((latest.value - previous.value).toFixed(2)) : null;

  if (points.length === 0) {
    return (
      <div className="formula-result-card">
        <h4>{title}</h4>
        <p className="muted">
          {t("result.noHistoryYet")}
        </p>
      </div>
    );
  }

  const target = view.target ?? undefined;
  const values = points.map((p) => p.value);
  const allValues = target != null ? [...values, target] : values;
  const max = Math.max(...allValues, 1);
  const min = Math.min(...allValues, 0);
  const range = max - min || 1;
  const width = 560;
  const height = 160;
  const padX = 24;
  const padY = 16;

  const toXY = (index: number, value: number) => {
    const x =
      points.length > 1
        ? padX + (index / (points.length - 1)) * (width - padX * 2)
        : width / 2;
    const y = height - padY - ((value - min) / range) * (height - padY * 2);
    return [x, y] as const;
  };

  const linePoints = points.map((p, i) => toXY(i, p.value).join(",")).join(" ");
  const targetY =
    target != null ? height - padY - ((target - min) / range) * (height - padY * 2) : null;

  return (
    <div className="formula-result-card">
      <h4>{title}</h4>
      <div className="formula-trend-headline">
        <strong>{latest.value}</strong>
        <DeltaBadge delta={delta} />
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="formula-trend-chart">
        {targetY != null && (
          <>
            <line
              x1={padX}
              y1={targetY}
              x2={width - padX}
              y2={targetY}
              stroke="#d1d5db"
              strokeDasharray="4 4"
              strokeWidth={1}
            />
            <text x={width - padX} y={targetY - 4} textAnchor="end" fontSize="10" fill="#9ca3af">
              {t("result.targetValue", { target })}
            </text>
          </>
        )}
        <polyline
          points={linePoints}
          fill="none"
          stroke="#4a1c96"
          strokeWidth={2}
          strokeLinejoin="round"
        />
        {points.map((p, i) => {
          const [x, y] = toXY(i, p.value);
          return <circle key={i} cx={x} cy={y} r={3.5} fill="#4a1c96" />;
        })}
      </svg>
      <div className="formula-trend-axis">
        {points.map((p, i) => (
          <span key={i}>{formatTrendDate(p.at)}</span>
        ))}
      </div>
    </div>
  );
};

const BreakdownCard: React.FC<{ title: string; view: BreakdownView }> = ({
  title,
  view,
}) => {
  const { t } = useTranslation("formulaBuilder");
  const rows = [...(view.rows ?? [])].sort((a, b) => a.rank - b.rank);

  if (rows.length === 0) {
    return (
      <div className="formula-result-card">
        <h4>{title}</h4>
        <p className="muted">
          {view.dimension
            ? t("result.noBreakdownAvailableForDimension", { dimension: view.dimension })
            : t("result.noBreakdownAvailable")}
        </p>
      </div>
    );
  }

  const max = Math.max(...rows.map((r) => r.value), 1);

  return (
    <div className="formula-result-card">
      <h4>{title}</h4>
      <div className="formula-breakdown-list">
        {rows.map((row) => {
          const tl = TRAFFIC_LIGHT_COLORS[row.traffic_light] ?? TRAFFIC_LIGHT_COLORS.yellow;
          const pct = (row.value / max) * 100;
          return (
            <div key={row.group} className="formula-breakdown-row">
              <span className="formula-breakdown-label">{row.group}</span>
              <div className="bar-track formula-breakdown-track">
                <div
                  className="formula-breakdown-fill"
                  style={{ width: `${pct}%`, background: tl.color }}
                />
              </div>
              <span className="formula-breakdown-value">{row.value}</span>
            </div>
          );
        })}
      </div>
      <p className="muted formula-result-caption">
        {view.dimension
          ? t("result.evaluatedPer", { dimension: view.dimension })
          : t("result.groupFallback")}
      </p>
    </div>
  );
};

const ComponentsCard: React.FC<{ title: string; view: ComponentsView }> = ({
  title,
  view,
}) => {
  const { t } = useTranslation("formulaBuilder");
  const items = view.components ?? [];

  return (
    <div className="formula-result-card">
      <h4>{title}</h4>
      <div className="formula-components-list">
        {items.map((item, index) => (
          <div key={item.ref ?? index} className="formula-components-row">
            <div className="formula-components-info">
              <span className="formula-components-label">{item.label}</span>
              <span className="formula-components-weight">
                {t("result.weightLabel", { percent: Math.round(item.share_percent) })}
              </span>
            </div>
            <strong>{item.final_value}</strong>
          </div>
        ))}
        <div className="formula-components-row formula-components-row--total">
          <span className="formula-components-label">{t("result.weightedIndex")}</span>
          <strong>{view.result}</strong>
        </div>
      </div>
    </div>
  );
};

type Props = {
  formulaName: string;
  resultViews: ResultViewsMap | null;
  activeTab: ResultViewKey;
  onTabChange: (key: ResultViewKey) => void;
  pinnedViews: Set<ResultViewKey>;
  pinningView: ResultViewKey | null;
  addingAll: boolean;
  onAddView: (view: ResultViewKey) => void;
  onAddAllViews: () => void;
};

const FormulaResultSection: React.FC<Props> = ({
  formulaName,
  resultViews,
  activeTab,
  onTabChange,
  pinnedViews,
  pinningView,
  addingAll,
  onAddView,
  onAddAllViews,
}) => {
  const { t } = useTranslation("formulaBuilder");

  const RESULT_TABS: { key: ResultViewKey; label: string }[] = [
    { key: "value_vs_target", label: t("result.tabs.valueVsTarget") },
    { key: "trend", label: t("result.tabs.trend") },
    { key: "breakdown", label: t("result.tabs.breakdown") },
    { key: "components", label: t("result.tabs.components") },
  ];

  const RESULT_DESCRIPTIONS: Record<ResultViewKey, string> = {
    value_vs_target: t("result.descriptions.valueVsTarget"),
    trend: t("result.descriptions.trend"),
    breakdown: t("result.descriptions.breakdown"),
    components: t("result.descriptions.components"),
  };

  const title = formulaName || t("result.defaultFormulaName");

  return (
    <div className="formula-result-section">
      <h2 className="formula-result-title">{t("result.heading")}</h2>

      {!resultViews ? (
        <div className="formula-result-empty">
          <InboxOutlined className="formula-result-empty-icon" />
          <p className="formula-result-empty-title">{t("result.emptyTitle")}</p>
          <p className="formula-result-empty-text">
            {t("result.emptyDescription")}
          </p>
        </div>
      ) : (
        <>
          <div className="formula-result-tabs">
            {RESULT_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={activeTab === tab.key ? "active" : ""}
                onClick={() => onTabChange(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="formula-result-body">
            <div className="formula-result-side">
              <p className="formula-result-description">
                {RESULT_DESCRIPTIONS[activeTab]}
              </p>
              <div className="formula-result-actions">
                <button
                  type="button"
                  className="formula-result-btn formula-result-btn--outline"
                  onClick={onAddAllViews}
                  disabled={addingAll}
                >
                  {addingAll ? "Adding..." : "Add all four views to dashboard"}
                </button>
                <button
                  type="button"
                  className="formula-result-btn formula-result-btn--solid"
                  onClick={() => onAddView(activeTab)}
                  disabled={pinningView === activeTab || pinnedViews.has(activeTab)}
                >
                  {pinnedViews.has(activeTab)
                    ? "Added to dashboard"
                    : pinningView === activeTab
                      ? "Adding..."
                      : "Add view to dashboard"}
                </button>
              </div>
            </div>

            <div className="formula-result-main">
              {activeTab === "value_vs_target" && (
                <ValueVsTargetCard title={title} view={resultViews.value_vs_target} />
              )}
              {activeTab === "trend" && (
                <TrendCard title={title} view={resultViews.trend} />
              )}
              {activeTab === "breakdown" && (
                <BreakdownCard title={title} view={resultViews.breakdown} />
              )}
              {activeTab === "components" && (
                <ComponentsCard title={title} view={resultViews.components} />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FormulaResultSection;
